import { AssetContainer } from "@babylonjs/core/assetContainer";
import { Bone } from "@babylonjs/core/Bones/bone";
import { Skeleton } from "@babylonjs/core/Bones/skeleton";
import { BoundingInfo } from "@babylonjs/core/Culling/boundingInfo";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Logger } from "@babylonjs/core/Misc/logger";
import { Progress } from "./progress";
/**
 * @internal
 * Base class of loader for MMD model (pmx / pmd / bpmx)
 */
export class MmdModelLoader {
    /**
     * Name of the loader
     */
    name;
    /**
     * Extensions supported by this loader
     */
    extensions;
    materialBuilder;
    useSdef;
    buildSkeleton;
    buildMorph;
    boundingBoxMargin;
    alwaysSetSubMeshesBoundingInfo;
    preserveSerializationData;
    _loggingEnabled;
    /** @internal */
    log;
    /** @internal */
    warn;
    /** @internal */
    error;
    /**
     * Shared material builder instance
     */
    static SharedMaterialBuilder = null;
    /**
     * Create a new MMD model loader
     *
     * @param name Name of the loader
     * @param extensions Extensions supported by this loader
     * @param options babylon.js scene loader options
     * @param loaderOptions Overriding options, typically pass global mmd model loader instance as loaderOptions
     */
    constructor(name, extensions, options = {}, loaderOptions) {
        this.name = name;
        this.extensions = extensions;
        loaderOptions = loaderOptions ?? {
            materialBuilder: MmdModelLoader.SharedMaterialBuilder,
            useSdef: true,
            buildSkeleton: true,
            buildMorph: true,
            boundingBoxMargin: 10,
            alwaysSetSubMeshesBoundingInfo: true,
            preserveSerializationData: false,
            loggingEnabled: false
        };
        this.materialBuilder = options.materialBuilder ?? loaderOptions.materialBuilder;
        this.useSdef = options.useSdef ?? loaderOptions.useSdef;
        this.buildSkeleton = options.buildSkeleton ?? loaderOptions.buildSkeleton;
        this.buildMorph = options.buildMorph ?? loaderOptions.buildMorph;
        this.boundingBoxMargin = options.boundingBoxMargin ?? loaderOptions.boundingBoxMargin;
        this.alwaysSetSubMeshesBoundingInfo = options.alwaysSetSubMeshesBoundingInfo ?? loaderOptions.alwaysSetSubMeshesBoundingInfo;
        this.preserveSerializationData = options.preserveSerializationData ?? loaderOptions.preserveSerializationData;
        this._loggingEnabled = options.loggingEnabled ?? loaderOptions.loggingEnabled;
        if (this._loggingEnabled) {
            this.log = this._logEnabled;
            this.warn = this._warnEnabled;
            this.error = this._errorEnabled;
        }
        else {
            this.log = this._logDisabled;
            this.warn = this._warnDisabled;
            this.error = this._errorDisabled;
        }
    }
    importMeshAsync(_meshesNames, scene, data, rootUrl, onProgress, _fileName) {
        return this._loadInternalAsync(scene, null, data, rootUrl, onProgress);
    }
    loadAsync(scene, data, rootUrl, onProgress, _fileName) {
        return this._loadInternalAsync(scene, null, data, rootUrl, onProgress).then(() => {
            return;
        });
    }
    loadAssetContainerAsync(scene, data, rootUrl, onProgress, _fileName) {
        const assetContainer = new AssetContainer(scene);
        return this._loadInternalAsync(scene, assetContainer, data, rootUrl, onProgress).then(() => {
            return assetContainer;
        });
    }
    async _loadInternalAsync(scene, assetContainer, state, rootUrl, onProgress) {
        const modelObject = await this._parseFileAsync(state.arrayBuffer);
        const progress = new Progress(true, this._getProgressTaskCosts(state, modelObject), onProgress ?? null);
        progress.endTask("Parse");
        progress.invokeProgressEvent();
        const buildGeometryResult = await this._buildGeometryAsync(state, modelObject, scene, assetContainer, progress);
        const rootMesh = buildGeometryResult.rootMesh;
        const textureNameMap = state.preserveSerializationData ? new Map() : null;
        const { materials, multiMaterials, textureLoadPromise } = await this._buildMaterialAsync(state, modelObject, rootMesh, buildGeometryResult.meshes, textureNameMap, scene, assetContainer, rootUrl, progress);
        const bonesMetadata = [];
        let skeleton = null;
        if (state.buildSkeleton) {
            skeleton = await this._buildSkeletonAsync(state, modelObject, buildGeometryResult.meshes, scene, assetContainer, bonesMetadata, progress);
        }
        else {
            progress.endTask("Build Skeleton");
        }
        const buildMorphResult = state.buildMorph
            ? await this._buildMorphAsync(state, modelObject, buildGeometryResult, scene, assetContainer, progress)
            : null;
        this._applyBoundingBoxToSubmeshes(buildGeometryResult.meshes, state.boundingBoxMargin);
        rootMesh.metadata = {
            isMmdModel: true,
            header: {
                modelName: modelObject.header.modelName,
                englishModelName: modelObject.header.englishModelName,
                comment: modelObject.header.comment,
                englishComment: modelObject.header.englishComment
            },
            bones: bonesMetadata,
            morphs: buildMorphResult?.morphsMetadata ?? [],
            rigidBodies: modelObject.rigidBodies,
            joints: modelObject.joints,
            meshes: buildGeometryResult.meshes,
            materials: materials,
            skeleton: skeleton
        };
        if (state.preserveSerializationData) {
            const materialsMetadata = [];
            const materials = modelObject.materials;
            for (let i = 0; i < materials.length; ++i) {
                const material = materials[i];
                materialsMetadata.push({
                    englishName: material.englishName,
                    comment: material.comment,
                    flag: material.flag
                });
            }
            rootMesh.metadata = {
                ...rootMesh.metadata,
                containsSerializationData: true,
                textureNameMap: textureNameMap,
                materialsMetadata: materialsMetadata,
                displayFrames: modelObject.displayFrames
            };
        }
        progress.invokeProgressEvent();
        await textureLoadPromise;
        progress.endTask("Texture Load");
        progress.invokeProgressEvent();
        rootMesh.setEnabled(true);
        const uniqueMeshes = [...new Set([rootMesh, ...buildGeometryResult.meshes])];
        if (assetContainer !== null) {
            assetContainer.rootNodes.push(rootMesh);
            assetContainer.meshes.push(...uniqueMeshes);
            assetContainer.geometries.push(...buildGeometryResult.geometries);
            assetContainer.materials.push(...materials);
            assetContainer.multiMaterials.push(...multiMaterials);
            if (skeleton !== null)
                assetContainer.skeletons.push(skeleton);
            if (buildMorphResult !== null)
                assetContainer.morphTargetManagers.push(...buildMorphResult.morphTargetManagers);
        }
        return {
            meshes: uniqueMeshes,
            particleSystems: [],
            skeletons: skeleton !== null ? [skeleton] : [],
            animationGroups: [],
            transformNodes: [],
            geometries: buildGeometryResult.geometries,
            lights: [],
            spriteManagers: []
        };
    }
    _getProgressTaskCosts(state, modelObject) {
        return [
            { name: "Parse", cost: Math.floor(state.arrayBuffer.byteLength / 100) },
            { name: "Build Material", cost: 100 * modelObject.materials.length },
            { name: "Build Skeleton", cost: state.buildSkeleton ? 100 * modelObject.bones.length : 0 },
            { name: "Texture Load", cost: 30000 * modelObject.textures.length }
        ];
    }
    async _buildSkeletonAsync(state, modelObject, meshes, scene, assetContainer, bonesMetadata, progress) {
        const preserveSerializationData = state.preserveSerializationData;
        scene._blockEntityCollection = !!assetContainer;
        const skeleton = new Skeleton(modelObject.header.modelName, modelObject.header.modelName + "_skeleton", scene);
        skeleton._parentContainer = assetContainer;
        scene._blockEntityCollection = false;
        {
            const bonesInfo = modelObject.bones;
            const bones = [];
            const looped = [];
            for (let i = 0; i < bonesInfo.length; ++i) {
                const boneInfo = bonesInfo[i];
                let isLooped = false;
                if (0 <= boneInfo.parentBoneIndex && boneInfo.parentBoneIndex < bonesInfo.length) {
                    let parentBoneIndex = boneInfo.parentBoneIndex;
                    for (let depthLimit = 0; depthLimit < bonesInfo.length && parentBoneIndex !== -1; ++depthLimit) {
                        if (parentBoneIndex === i) {
                            isLooped = true;
                            this.warn(`Bone loop detected. Ignore Parenting. Bone index: ${i}`);
                            break;
                        }
                        parentBoneIndex = bonesInfo[parentBoneIndex].parentBoneIndex;
                    }
                    if (i <= boneInfo.parentBoneIndex) {
                        this.warn(`Parent bone index is greater equal than child bone index. Bone index: ${i} Parent bone index: ${boneInfo.parentBoneIndex}`);
                    }
                }
                else {
                    if (boneInfo.parentBoneIndex !== -1) {
                        this.error(`Parent bone index is out of range. Bone index: ${i} Parent bone index: ${boneInfo.parentBoneIndex}`);
                    }
                }
                const boneWorldPosition = boneInfo.position;
                const bonePosition = new Vector3(boneWorldPosition[0], boneWorldPosition[1], boneWorldPosition[2]);
                if ((0 <= boneInfo.parentBoneIndex && boneInfo.parentBoneIndex < bonesInfo.length) && !isLooped) {
                    const parentBoneInfo = bonesInfo[boneInfo.parentBoneIndex];
                    bonePosition.x -= parentBoneInfo.position[0];
                    bonePosition.y -= parentBoneInfo.position[1];
                    bonePosition.z -= parentBoneInfo.position[2];
                }
                const boneMatrix = Matrix.Identity()
                    .setTranslation(bonePosition);
                const bone = new Bone(boneInfo.name, skeleton, undefined, boneMatrix, undefined, undefined, i // bone index
                );
                bones.push(bone);
                looped.push(isLooped);
                const boneMetadata = {
                    name: boneInfo.name,
                    englishName: boneInfo.englishName,
                    parentBoneIndex: boneInfo.parentBoneIndex,
                    transformOrder: boneInfo.transformOrder,
                    flag: boneInfo.flag,
                    appendTransform: boneInfo.appendTransform,
                    axisLimit: boneInfo.axisLimit,
                    ik: boneInfo.ik,
                    ...preserveSerializationData ? {
                        tailPosition: boneInfo.tailPosition,
                        localVector: boneInfo.localVector,
                        externalParentTransform: boneInfo.externalParentTransform
                    } : undefined
                };
                bonesMetadata.push(boneMetadata);
            }
            for (let i = 0; i < bones.length; ++i) {
                const boneInfo = bonesInfo[i];
                const bone = bones[i];
                if ((0 <= boneInfo.parentBoneIndex && boneInfo.parentBoneIndex < bones.length) && !looped[i]) {
                    bone.setParent(bones[boneInfo.parentBoneIndex], false);
                }
            }
            for (let i = 0; i < bones.length; ++i) {
                const bone = bones[i];
                if (bone.getParent() === null) {
                    bone._updateAbsoluteBindMatrices();
                }
            }
        }
        progress.endTask("Build Skeleton");
        progress.invokeProgressEvent();
        for (let i = 0; i < meshes.length; ++i)
            meshes[i].skeleton = skeleton;
        return skeleton;
    }
    _applyBoundingBoxToSubmeshes(meshes, boundingBoxMargin) {
        // we created SubMesh without bounding box, for setBoundingInfo manually
        // We assign the same bounding box to all submeshes
        // because this ensures that the order of the submeshes is preserved when the engine sorts the transparent draw order.
        for (let i = 0; i < meshes.length; ++i) {
            const mesh = meshes[i];
            if (mesh.subMeshes === undefined)
                continue;
            if (1 < mesh.subMeshes.length || !mesh.subMeshes[0].IsGlobal || this.alwaysSetSubMeshesBoundingInfo) {
                const boundingInfo = mesh.getBoundingInfo();
                const minimum = boundingBoxMargin !== 0
                    ? new Vector3().setAll(-boundingBoxMargin).addInPlace(boundingInfo.minimum)
                    : boundingInfo.minimum;
                const maximum = boundingBoxMargin !== 0
                    ? new Vector3().setAll(boundingBoxMargin).addInPlace(boundingInfo.maximum)
                    : boundingInfo.maximum;
                const subMeshes = mesh.subMeshes;
                for (let i = 0; i < subMeshes.length; ++i) {
                    subMeshes[i].setBoundingInfo(new BoundingInfo(minimum, maximum));
                }
            }
            if (boundingBoxMargin !== 0) {
                const boundingInfo = mesh.getBoundingInfo();
                mesh.setBoundingInfo(new BoundingInfo(new Vector3().setAll(-boundingBoxMargin).addInPlace(boundingInfo.minimum), new Vector3().setAll(boundingBoxMargin).addInPlace(boundingInfo.maximum)));
            }
        }
    }
    /**
     * Enable or disable debug logging (default: false)
     */
    get loggingEnabled() {
        return this._loggingEnabled;
    }
    set loggingEnabled(value) {
        this._loggingEnabled = value;
        if (value) {
            this.log = this._logEnabled;
            this.warn = this._warnEnabled;
            this.error = this._errorEnabled;
        }
        else {
            this.log = this._logDisabled;
            this.warn = this._warnDisabled;
            this.error = this._errorDisabled;
        }
    }
    _logEnabled(message) {
        Logger.Log(message);
    }
    _logDisabled() {
        // do nothing
    }
    _warnEnabled(message) {
        Logger.Warn(message);
    }
    _warnDisabled() {
        // do nothing
    }
    _errorEnabled(message) {
        Logger.Error(message);
    }
    _errorDisabled() {
        // do nothing
    }
}
